import * as Doms from "oc-ui/Doms";
import * as Scenes from "oc-threejs/Scenes";
import * as Screens from "oc-threejs/Screens";
import { v3, v2, quat } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import { DeviceDetection } from "oc-commons/DeviceDetection";
import { Draggels, DragContext, DraggelsInstance } from "oc-interaction/Draggels";

import * as _ from "lodash";
import { Object3D } from "three";

/**
 * Der BaseInteractionHandler3D behandelt Tap, Hover und Drag&Drop von 3D-Objekten innerhalb der Szene sowie
 * Rein- und Rausdraggen von 3D-Objekten in/aus der Szene sowie von 2D-Objekten (z.B. Materialeigenschaften)
 * in die Szene.
 * Der BaseInteractionHandler3D überlässt das konkrete Drag/Drop-Verhalten im 3D-Raum abgeleiteten Klassen,
 * also wie genau Objekte im Raum positioniert und orientiert werden.
 */
export class BaseInteractionHandler3D {
  stageEventNames = [
    "tapDraggable",
    "tapObject3D",
    "tapSceneOnly",
    "drag2dBegin",
    "drag2dSceneEnter",
    "drag2d",
    "drop2d",
    "drag2dCancel",
    "drag2dSceneLeave",
    "drag2dEnd",
    "drag3dBegin",
    "drag3dSceneEnter",
    "drag3dSceneEnterImmediate",
    "drag3d",
    "drop3d",
    "drag3dSceneLeave",
    "drag3dCancel",
    "drag3dEnd",
    "dragRootContainerEnter",
    "dragRootContainerLeave",
  ];

  event: PointerEvent | null = null;
  dragContext: DragContext | null = null;
  dragSourceWasScene = false;
  sourceDraggable: any = null;
  eventIsOverScene = false;
  dragData: any = null;
  dragDomElement: HTMLElement | null = null;
  dragDomElementVisible = false;
  dragObject3d: Object3D | null = null;
  dragObject3dMemento: any = null;
  receiveDropableObject3d: any = null; // Type depends on extensions
  receiveDropableObject3dHovered: any = null;
  tapObject3d: any = null;
  hoveredObject3d: any = null;
  interactionType = "undecided";
  dragSceneEnterTriggered = false;
  dragSceneEnterImmediateTriggered = false;
  dragObject3dExistedBefore = false;
  wasCamerlockedByMe = false;
  origin = {
    x: 0,
    y: 0,
    t: 0,
  };
  noTapTimer: number | null = null;
  orbitcontrolCameraMovement = false;
  locked = false;

  rootContainer: HTMLElement;
  config: any;
  draggels!: DraggelsInstance;
  target: any;

  // Debug properties from original code
  PC__: any;
  POSITION__: any;

  constructor(rootContainer: HTMLElement = document.body) {
    this.rootContainer = rootContainer;
    if (!rootContainer) {
      console.warn("no rootContainer");
      // @ts-ignore
      debugger;
    }
    this.config = STAGE.settings.dragDrop || {};
    STAGE.on("animatorTick", (now: number, tags: Set<string>) => this.onAnimatorTick(now, tags));
    this._resetHandle();
  }

  draggable(element: HTMLElement) {
    const call: any = {
      getDragData: (handle: any) => {},
      getDragDomElement: (handle: any) => element.cloneNode(true),
      getDragObject3D: (handle: any) => null,
      onTap: (handle: any) => {},
      isDraggable: () => true,
    };

    const dragContext = {
      setDragData: (callback: any) => {
        call.getDragData = callback;
        return dragContext;
      },
      setDragDomElement: (callback: any) => {
        call.getDragDomElement = callback;
        return dragContext;
      },
      setDragObject3D: (callback: any) => {
        call.getDragObject3D = callback;
        return dragContext;
      },
      onTap: (callback: any) => {
        call.onTap = callback;
        return dragContext;
      },
      isDraggable: (callback: any) => {
        call.isDraggable = callback;
        return dragContext;
      },
      call,
    };

    if (!(element instanceof HTMLElement)) {
      // @ts-ignore
      debugger;
      throw new Error("only dom elements can be draggable");
    }
    Doms.setData(element, "customDraggable", dragContext);

    element.style.touchAction = "none";

    return dragContext;
  }

  setSceneDroppable() {
    STAGE.orbitcontrols.addEventListener("start", () => {
      this.orbitcontrolCameraMovement = true;
      this._setCursor(null);
    });
    STAGE.orbitcontrols.addEventListener("end", () => (this.orbitcontrolCameraMovement = false));

    Doms.setData(STAGE.canvas, "isStageCanvas", true);
    STAGE.canvas.parentElement!.style.touchAction = "none";

    const logging = false;

    const ifActive = (callback: Function) => {
      return this.draggels.ifActive((...args: any[]) => {
        if (logging) {
          console.log(...args);
        }
        if (!this.locked) {
          callback(...args);
        }
      });
    };

    this.draggels = Draggels("InteractionHandler");

    this.draggels.addDragStartListener(
      this.rootContainer,
      ifActive((dragContext: DragContext, event: PointerEvent) => {
        this._resetHandle();
        this._setEvent(dragContext, event);
        this._setOriginFromEvent();
        this.dragSourceWasScene = this.eventIsOverScene;
        if (this.dragSourceWasScene) {
          this.fetchObjectsInSceneForDragTap();
          if (this.dragObject3d) {
            this._lockCamera(event);
            this.dragObject3dExistedBefore = true;
          }
          if (this.dragObject3d && this.tapObject3d) {
            this.interactionType = "undecided";
          } else if (this.dragObject3d) {
            this.interactionType = "drag3d";
            this._triggerDrag3dBegin(event);
          } else {
            this.interactionType = "tap";
          }
        } else {
          this.sourceDraggable = Doms.getData(this.target, "customDraggable");
          if (this.sourceDraggable && this.sourceDraggable.call.isDraggable()) {
            this.interactionType = "undecided";
          } else {
            this.interactionType = "none";
          }
        }
        if (this.interactionType === "undecided") {
          this._startNoTapTimer();
        }

        if (this.eventIsOverScene) {
          if (this.interactionType === "drag2d" || this.interactionType === "drag3d") {
            this._maybeTriggerDragSceneEnter(event);
          }
        }
      }) as any, // Cast to match expected type if needed
    );

    this.draggels.addDragEndListener(
      ifActive((dragContext: DragContext, event: PointerEvent) => {
        this._setEvent(dragContext, event);
        this._unlockCamera();
        this._cancelNoTapTimer();

        if (this.interactionType === "undecided") {
          if (this._outOfTapTresholds()) {
            this._itWasADragDecideIf2dOr3d(event);
          } else {
            this.interactionType = "tap";
          }
        }

        if (this.interactionType === "tap") {
          if (!this._outOfTapTresholds()) {
            if (this.eventIsOverScene) {
              if (this.tapObject3d) {
                this._triggerTapObject3D(event);
              } else {
                this._triggerTapSceneOnly(event);
              }
            } else {
              if (this.sourceDraggable) {
                this._triggerTapDraggable(event);
              }
            }
          }
        } else if (this.interactionType === "drag2d") {
          if (this.eventIsOverScene) {
            this._maybeTriggerDragSceneEnter(event);
            this._triggerDrag2d(event);
            if (this.receiveDropableObject3d) {
              this._triggerDrop2d(event);
            } else {
              this._triggerDrag2dCancel(event);
            }
          } else {
            this._triggerDrag2dCancel(event);
          }
          this._triggerDrag2dEnd(event);
        } else if (this.interactionType === "drag3d") {
          if (this.eventIsOverScene) {
            this._maybeTriggerDragSceneEnter(event);
            this._triggerDrag3d(event);
            this._triggerDrop3d(event);
          } else {
            this._triggerDrag3dCancel(event);
          }
          this._triggerDrag3dEnd(event);
        }

        this._resetHandle();
      }) as any,
    );

    this.draggels.addDragMoveListener(
      ifActive((dragContext: DragContext, event: PointerEvent) => {
        this._setEvent(dragContext, event);

        if (this.interactionType === "undecided") {
          if (this._outOfTapTresholds()) {
            this._itWasADragDecideIf2dOr3d(event);
          }
        }

        if (this.interactionType === "drag2d") {
          this._triggerDrag2d(event);
        } else if (this.interactionType === "drag3d") {
          this._maybeTriggerDragSceneEnter(event);
          this._triggerDrag3d(event);
        }
      }) as any,
    );

    if (this.config.cursorChangesOverDraggables) {
      document.body.addEventListener("pointermove", (event) => {
        if (
          !this.orbitcontrolCameraMovement &&
          this.interactionType !== "drag2d" &&
          this.interactionType !== "drag3d"
        ) {
          this._setCursor(event);
        }
      });
    }

    this.draggels.addDragEnterListener(
      STAGE.canvas,
      ifActive((dragContext: DragContext, event: PointerEvent) => {
        this._setEvent(dragContext, event);
        if (this.interactionType === "undecided") {
          this._itWasADragDecideIf2dOr3d(event);
        }
        if (this.interactionType === "drag2d" || this.interactionType === "drag3d") {
          this._maybeTriggerDragSceneEnter(event);
        }
      }) as any,
    );

    this.draggels.addDragLeaveListener(
      STAGE.canvas,
      ifActive((dragContext: DragContext, event: PointerEvent) => {
        this._setEvent(dragContext, event);
        if (this.interactionType === "drag2d" || this.interactionType === "drag3d") {
          this._triggerDragSceneLeave(true);
        }
      }) as any,
    );
  }

  getInteractingObjects3D() {
    return STAGE.scene.children;
  }

  getDragDataFromObject3D(dragObject3d: Object3D) {
    return dragObject3d.userData;
  }

  getDragDomElementFromDragData(dragData: any) {
    return Doms.createElementFromHTML(
      '<div style="border:1px solid black; background-color: white; padding: 1em; font-size: 2em;" class="draghandle">?</div>',
    );
  }

  setDragObject3dPosition(dropped: boolean) {
    if (this.eventIsOverScene && this.dragObject3d) {
      const point = Screens.rayCastAtMouseOnPlane(this.event!, Maths3.plane.y0);
      if (point && this.dragObject3d.parent) {
        const bounds = Scenes.boundingBox(this.dragObject3d);
        point.setY(bounds.getSize(v3()).y / 2);
        this.dragObject3d.parent.worldToLocal(point);
        this.dragObject3d.position.copy(point);
        STAGE.dirty();
      }
    }
  }

  maybeUpdate() {}

  isDraggable(object: any) {
    return object.draggable;
  }

  isTapable(object: any) {
    return object.tapable;
  }

  onTapDraggable(event?: any) {}

  onTapObject3D(event: any): any {}

  onTapSceneOnly(event: any) {}

  onDrag2dBegin(event: any) {}

  onDrag2d(event: any) {}

  onDrop2d(event: any) {}

  onDrag2dCancel(event: any) {}

  onDrag2dEnd(event: any) {}

  onDrag3dBegin(event: any) {}

  onDrag3d(event: any) {}

  onDrop3d(event: any) {}

  onDrag3dCancel(event: any) {}

  onDrag3dEnd(event: any) {}

  onDrag3dSceneEnter(event: any) {}

  onDrag3dSceneEnterImmediate(event: any) {}

  onDrag2dSceneEnter(event: any) {}

  onDrag3dSceneLeave(real: boolean) {}

  onDrag2dSceneLeave(real: boolean) {}

  onDragRootContainerEnter(event: any) {}

  onDragRootContainerLeave(event: any) {}

  onAnimatorTick(now: number, tags: Set<string>) {}

  onTouch() {}

  fetchObjectsInSceneForDragTap() {
    this.tapObject3d = this.getTapObject3dFromScene();
    this.dragObject3d = this.getDragObject3dFromScene();
    if (this.dragObject3d) {
      this.dragData = this.getDragDataFromObject3D(this.dragObject3d);
      this._hideDragDomElement();
      this.dragDomElement = this.getDragDomElementFromDragData(this.dragData);
    }
  }

  fetchObjectsInSceneForDrag2d() {
    this.receiveDropableObject3d = this.getReceiveDropableObject3dFromScene();
  }

  getTapObject3dFromScene() {
    const interactingObjects = this.getInteractingObjects3D();
    return Scenes.findUp(
      STAGE.changeRaycastVisibilityDuring("tap", () =>
        Screens.fetchObjectAtMouse(this.event!, interactingObjects),
      ),
      (o: any) => this.isTapable(o),
    );
  }

  getDragObject3dFromScene(): Object3D | null {
    const interactingObjects = this.getInteractingObjects3D();
    return Scenes.findUp(
      STAGE.changeRaycastVisibilityDuring("drag", () =>
        Screens.fetchObjectAtMouse(this.event!, interactingObjects),
      ),
      (o: any) => this.isDraggable(o),
    ) as Object3D | null;
  }

  getReceiveDropableObject3dFromScene() {
    const interactingObjects = this.getInteractingObjects3D();
    return Scenes.findUp(
      STAGE.changeRaycastVisibilityDuring("dragReceiveDropable", () =>
        Screens.fetchObjectAtMouse(this.event!, interactingObjects),
      ),
      (o: any) => o.receiveDropable && o.acceptReceiveDrop(this.dragData),
    );
  }

  createDragObjectsFromDraggable() {
    this.dragData = this.sourceDraggable.call.getDragData(this);
    this._hideDragDomElement();
    this.dragDomElement = Doms.createElementFromHTML('<div class="draghandle"></div>');
    this.dragDomElement!.appendChild(this.sourceDraggable.call.getDragDomElement(this));
    this.dragObject3d = this.sourceDraggable.call.getDragObject3D(this);
    if (!(this.interactionType != "drag2d" && this.eventIsOverScene)) {
      this._showDragDomElement();
    }
  }

  _resetHandle() {
    this._hideDragDomElement();
    this._cancelNoTapTimer();
    this.event = null;
    this.dragContext = null;
    this.dragSourceWasScene = false;
    this.sourceDraggable = null;
    this.eventIsOverScene = false;
    this.dragDomElement = null;
    this.dragDomElementVisible = false;
    this.dragData = null;
    this.dragObject3d = null;
    this.dragObject3dMemento = null;
    this.receiveDropableObject3d = null;
    this.receiveDropableObject3dHovered = null;
    this.tapObject3d = null;
    this.interactionType = "undecided";
    this.dragSceneEnterTriggered = false;
    this.dragSceneEnterImmediateTriggered = false;
    this.dragObject3dExistedBefore = false;
    this.wasCamerlockedByMe = false;
    this.origin = {
      x: 0,
      y: 0,
      t: 0,
    };
    this.noTapTimer = null;
  }

  _itWasADragDecideIf2dOr3d(event: any) {
    if (this.dragSourceWasScene) {
      this.interactionType = "drag3d";
      this._triggerDrag3dBegin(event);
    } else if (this.sourceDraggable) {
      this.createDragObjectsFromDraggable();
      if (this.dragObject3d) {
        this.interactionType = "drag3d";
        this._triggerDrag3dBegin(event);
      } else {
        this.interactionType = "drag2d";
        this._triggerDrag2dBegin(event);
      }
    }
  }

  _setOriginFromEvent() {
    this.origin.x = this._mx();
    this.origin.y = this._my();
    this.origin.t = this.event!.timeStamp;
  }

  _maybeTriggerDragSceneEnter(event: any) {
    if (this.eventIsOverScene) {
      if (!this.dragSceneEnterImmediateTriggered) {
        this._triggerDragSceneEnterImmediate(event);
      }
      if (!this.dragSceneEnterTriggered) {
        this._triggerDragSceneEnter(event);
      }
    }
  }

  _triggerTapDraggable(event: any) {
    this.onTapDraggable(event);
    this.sourceDraggable.call.onTap();
    STAGE.trigger("tapDraggable", this);
  }

  _triggerTapObject3D(event: any) {
    const proceedWithTab = this.onTapObject3D(event);
    if (proceedWithTab === false) {
      return;
    }
    this.tapObject3d.onTap();
    STAGE.trigger("tapObject3D", this);
  }

  _triggerTapSceneOnly(event: any) {
    this.onTapSceneOnly(event);
    STAGE.trigger("tapSceneOnly", this);
  }

  _triggerDrag2dBegin(event: any) {
    this.draggels.claimDragStream();
    this._lockCamera(event);
    this._showCursorDragging(true);
    this.onDrag2dBegin(event);
    STAGE.trigger("drag2dBegin", this);
  }

  _triggerDrag2d(event: any) {
    this.fetchObjectsInSceneForDrag2d();
    this._setDragDomElementToPosition();
    if (this.receiveDropableObject3d !== this.receiveDropableObject3dHovered) {
      if (this.receiveDropableObject3dHovered) {
        this.receiveDropableObject3dHovered.hovering = false;
        this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
      }
      this.receiveDropableObject3dHovered = this.receiveDropableObject3d;
      if (this.receiveDropableObject3d) {
        this.receiveDropableObject3d.hovering = this.dragData || true;
        this.receiveDropableObject3d.onChangeInDragSelectHoverState();
      }
      STAGE.dirty();
    }
    this.onDrag2d(event);
    STAGE.trigger("drag2d", this);
  }

  _triggerDrop2d(event: any) {
    this._hideDragDomElement();
    this.onDrop2d(event);
    this.receiveDropableObject3d.onReceiveDrop(this.dragData);
    STAGE.trigger("drop2d", this);
  }

  _triggerDrag2dCancel(event: any) {
    this._hideDragDomElement();
    this.onDrag2dCancel(event);
    STAGE.trigger("drag2dCancel", this);
  }

  _triggerDrag2dEnd(event: any) {
    this._showCursorDragging(false);
    if (this.receiveDropableObject3dHovered) {
      this.receiveDropableObject3dHovered.hovering = false;
      this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
    }
    this.onDrag2dEnd(event);
    STAGE.trigger("drag2dEnd", this);
  }

  _triggerDrag3dBegin(event: any) {
    this.draggels.claimDragStream();
    this._lockCamera(event);
    this._showCursorDragging(true);
    if (this.dragObject3dExistedBefore && this.dragObject3d) {
      this.dragObject3dMemento = {
        raycastVisibleTags: (this.dragObject3d as any).raycastVisibleTags,
        position: this.dragObject3d.position.clone(),
        quaternion: this.dragObject3d.quaternion.clone(),
        scale: this.dragObject3d.scale.clone(),
        parent: this.dragObject3d.parent,
      };
    } else if (this.dragObject3d) {
      this.dragObject3dMemento = {
        raycastVisibleTags: (this.dragObject3d as any).raycastVisibleTags,
      };
    }
    if (this.dragObject3d) {
      (this.dragObject3d as any).dragging = true;
      (this.dragObject3d as any).onChangeInDragSelectHoverState();
      (this.dragObject3d as any).raycastVisibleTags = [];
      this.onDrag3dBegin(event);
      (this.dragObject3d as any).onDragBegin();
      (this.dragObject3d as any).trigger("dragBegin");
      STAGE.trigger("drag3dBegin", this);
      STAGE.animator.start("drag");
      STAGE.dirty();
    }
  }

  _triggerDrag3d(event: any) {
    this._setDragDomElementToPosition();
    this.setDragObject3dPosition(false);
    this.onDrag3d(event);
    if (this.dragObject3d) {
      (this.dragObject3d as any).onDrag(event);
      (this.dragObject3d as any).trigger("drag");
    }
    STAGE.trigger("drag3d", this);
    STAGE.dirty();
  }

  _triggerDrop3d(event: any) {
    this.setDragObject3dPosition(true);
    this.onDrop3d(event);
    if (this.dragObject3d) {
      (this.dragObject3d as any).raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
      (this.dragObject3d as any).dragging = false;
      (this.dragObject3d as any).onChangeInDragSelectHoverState();
      (this.dragObject3d as any).onDrop();
      (this.dragObject3d as any).trigger("drop");
    }
    STAGE.trigger("drop3d", this);
    STAGE.dirty();
  }

  _triggerDrag3dCancel(event: any) {
    if (this.dragObject3d) {
      if (this.dragObject3dExistedBefore) {
        (this.dragObject3d as any).raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
        this.dragObject3d.position.copy(this.dragObject3dMemento.position);
        this.dragObject3d.quaternion.copy(this.dragObject3dMemento.quaternion);
        this.dragObject3d.scale.copy(this.dragObject3dMemento.scale);
        this.dragObject3dMemento.parent.add(this.dragObject3d);
      } else {
        Scenes.remove(this.dragObject3d);
      }
      this.onDrag3dCancel(event);
      if (this.dragObject3dExistedBefore) {
        (this.dragObject3d as any).raycastVisibleTags = this.dragObject3dMemento.raycastVisibleTags;
      }
      (this.dragObject3d as any).dragging = false;
      (this.dragObject3d as any).onDragCancel();
      (this.dragObject3d as any).trigger("dragCancel");
    }
    STAGE.trigger("drag3dCancel", this);
    STAGE.dirty();
  }

  _triggerDrag3dEnd(event: any) {
    STAGE.animator.stop("drag");
    this._hideDragDomElement();
    this._showCursorDragging(false);
    this.onDrag3dSceneLeave(false);
    if (this.dragObject3d) {
      (this.dragObject3d as any).onDragLeave(false);
    }
    STAGE.trigger("drag3dSceneLeave", this, false);
    this.onDrag3dEnd(event);
    if (this.dragObject3d) {
      (this.dragObject3d as any).onDragEnd();
      (this.dragObject3d as any).trigger("dragEnd");
    }
    STAGE.trigger("drag3dEnd", this);
  }

  _triggerDragSceneEnter(event: any) {
    if (this.dragObject3d) {
      this._hideDragDomElement();
      if (Scenes.isOrphan(this.dragObject3d)) {
        if (this.dragObject3dExistedBefore && !Scenes.isOrphan(this.dragObject3dMemento.parent)) {
          this.dragObject3dMemento.parent.add(this.dragObject3d);
        } else {
          STAGE.scene.add(this.dragObject3d);
        }
      }
      this.setDragObject3dPosition(false);
      this.onDrag3dSceneEnter(event);
      (this.dragObject3d as any).onDragEnter();
      STAGE.trigger("drag3dSceneEnter", this);
      STAGE.dirty();
    } else {
      this.onDrag2dSceneEnter(event);
      STAGE.trigger("drag2dSceneEnter", this);
    }
    this.dragSceneEnterTriggered = true;
  }

  _triggerDragSceneEnterImmediate(event: any) {
    this.dragSceneEnterImmediateTriggered = true;
    this.onDrag3dSceneEnterImmediate(event);
    STAGE.trigger("drag3dSceneEnterImmediate", this);
  }

  _triggerDragSceneLeave(real: boolean) {
    if (this.dragObject3d) {
      if (real && this.dragObject3dExistedBefore) {
        this.dragObject3dMemento.parent = this.dragObject3d.parent;
      }
      this._showDragDomElement();
      Scenes.remove(this.dragObject3d);
      this.onDrag3dSceneLeave(real);
      (this.dragObject3d as any).onDragLeave(real);
      STAGE.trigger("drag3dSceneLeave", this, real);
      STAGE.dirty();
    } else {
      this.onDrag2dSceneLeave(real);
      STAGE.trigger("drag2dSceneLeave", this, real);
    }
    if (this.receiveDropableObject3dHovered) {
      this.receiveDropableObject3dHovered.hovering = false;
      this.receiveDropableObject3dHovered.onChangeInDragSelectHoverState();
      this.receiveDropableObject3dHovered = null;
    }
    this.dragSceneEnterImmediateTriggered = false;
    this.dragSceneEnterTriggered = false;
  }

  _triggerDragRootContainerEnter(event: any) {
    if (!this.dragObject3d || !this.eventIsOverScene) {
      this._showDragDomElement();
    }
    this.onDragRootContainerEnter(event);
    STAGE.trigger("dragRootContainerEnter", this);
  }

  _triggerDragRootContainerLeave(event: any) {
    this._hideDragDomElement();
    this.onDragRootContainerLeave(event);
    STAGE.trigger("dragRootContainerLeave", this);
  }

  _outOfTapTresholds() {
    return (
      this._distancePx() > this.config.clickPxTreshold ||
      (this._distanceMs() > this.config.clickMsTreshold && this._distancePx() < 2)
    );
  }

  _setEvent(dragContext: DragContext, event: PointerEvent) {
    this.event = event;
    this.dragContext = dragContext;
    this.target = this.event.target;
    this.eventIsOverScene = Doms.getData(this.target, "isStageCanvas") == true;
  }

  _showDragDomElement() {
    this.dragDomElementVisible = true;
    if (this.dragDomElement) {
      this.dragDomElement
        .querySelectorAll("*")
        .forEach((element: any) => (element.style.pointerEvents = "none"));
      this.dragDomElement.style.position = "absolute";
      this.dragDomElement.style.pointerEvents = "none";
      this.dragDomElement.style.zIndex = "1000";
      this.dragDomElement.style.opacity = "0.8";
      this._setDragDomElementToPosition();
      document.body.appendChild(this.dragDomElement);

      let originalSize = Doms.getData(this.dragDomElement, "originalSize");
      if (!originalSize) {
        const rect = this.dragDomElement.getBoundingClientRect();
        Doms.setData(this.dragDomElement, "originalSize", {
          width: rect.width,
          height: rect.height,
        });
        this._setDragDomElementToPosition();
      }
    }
  }

  _hideDragDomElement() {
    if (this.dragDomElement) {
      this.dragDomElementVisible = false;
      this.dragDomElement.style.opacity = "0";
      Doms.removeElement(this.dragDomElement);
    }
  }

  _lockCamera(event: any) {
    STAGE.cameralock = true;
    this.wasCamerlockedByMe = true;
    STAGE.orbitcontrols.enabled = false;
    STAGE.orbitcontrols.domElement.releasePointerCapture(event.pointerId);
  }

  _unlockCamera() {
    if (this.wasCamerlockedByMe) {
      STAGE.cameralock = false;
      this.wasCamerlockedByMe = false;
      STAGE.orbitcontrols.enabled = true;
    }
  }

  _mx() {
    return this.event ? this.event.pageX : 0;
  }

  _my() {
    return this.event ? this.event.pageY : 0;
  }

  _distancePx() {
    const dx = this._mx() - this.origin.x;
    const dy = this._my() - this.origin.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  _distanceMs() {
    return this.event ? this.event.timeStamp - this.origin.t : 0;
  }

  _setDragDomElementToPosition() {
    if (this.dragDomElement) {
      let originalSize = Doms.getData(this.dragDomElement, "originalSize") as {
        width: number;
        height: number;
      };
      const w = originalSize ? originalSize.width / 2 : 0;
      const h = originalSize ? originalSize.height / 2 : 0;
      this.dragDomElement.style.left = this._mx() - w + "px";
      this.dragDomElement.style.top = this._my() - h + "px";
    }
  }

  _startNoTapTimer() {
    this._cancelNoTapTimer();
    // Use window.setTimeout and cast result if needed or rely on NodeJS mismatch safety
    // Assuming browser env
    this.noTapTimer = window.setTimeout(() => {
      // Logic to clear undecided if needed?
      // Original code just had the timer handle logic elsewhere or was empty?
      // Original _startNoTapTimer only called _cancelNoTapTimer.
      // Wait, line 1136: this._cancelNoTapTimer()
      // Line 125: noTapTimer = null.
      // There is no setTimeout in _startNoTapTimer in original code?
      // Ah, original code:
      // _startNoTapTimer() { this._cancelNoTapTimer(); }
      // It seems it does NOT start a timer?
      // But _cancelNoTapTimer clears it.
      // Maybe it was intended to start one but didn't?
      // I will preserve logic: do nothing but cancel.
    }, 0);
    this._cancelNoTapTimer(); // Original logic just cancelled it?
    // Let me check original code lines 1135-1137
    // 1135: _startNoTapTimer() {
    // 1136:   this._cancelNoTapTimer();
    // 1137: }
    // Yes, it effectively does nothing but cancel the old one (if there was one).
    // And _cancelNoTapTimer clears it.
    // So I will just call _cancelNoTapTimer.
  }

  _cancelNoTapTimer() {
    if (this.noTapTimer) {
      window.clearTimeout(this.noTapTimer);
      this.noTapTimer = null;
    }
  }

  _showCursorDragging(on: boolean) {
    const dragclass = "cursorDragging";
    Doms.setClassIf(this.rootContainer, dragclass, on);
  }

  _setCursor(event: any) {
    const canvas = STAGE.canvas;
    let hover =
      !this.orbitcontrolCameraMovement &&
      STAGE.changeRaycastVisibilityDuring("hover", () =>
        Screens.fetchObjectAtMouse(event, this.getInteractingObjects3D()),
      );
    // @ts-ignore
    hover = Scenes.findUp(hover, (object: any) => object.hoverable);
    if (hover) {
      if (this.hoveredObject3d === hover) {
        return;
      }
      if (this.hoveredObject3d) {
        this.hoveredObject3d.hovering = false;
        this.hoveredObject3d.onChangeInDragSelectHoverState();
      }
      this.hoveredObject3d = hover;
      canvas.style.cursor = "pointer";
      (this.hoveredObject3d as any).hovering = true;
      (this.hoveredObject3d as any).onChangeInDragSelectHoverState();
    } else {
      canvas.style.cursor = "default";
      if (this.hoveredObject3d) {
        this.hoveredObject3d.hovering = false;
        this.hoveredObject3d.onChangeInDragSelectHoverState();
        this.hoveredObject3d = null;
      }
    }
  }
}
